import { computed, inject, Injectable, Signal } from '@angular/core';
import { MapViewerInitOptions, MapViewerService } from '@3ddv/ngx-dvm-internal';
import { MapViewerLoadMapOptions, MapViewerFlagsAPI, MapViewerLoadObject, MapViewerPlugins, MapViewerStylesLevel, MapViewerNode } from '@3ddv/dvm-internal';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '@configuration/configuration';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';
import { catchError, first, from, Observable, of, tap } from 'rxjs';
import { NavigationService } from '@services/navigation.service';
import { VIEWER } from '@dvm.providers';
import { ViewerStylesOptions } from '@models/interfaces/dvm/ViewerStylesOptions.interface';
import { ViewService } from '@services/view.service';
import { FilterService } from '@services/filter.service';
import { Filter } from '@models/types/app/Filter.type';
import { SeatmapDisplay } from '@models/types/components/SeatmapDisplay.type';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import { TemplateService } from '@services/template.service';
import { CustomAvailability, RandomAvailability } from '@models/types/components/Availability.type';
import { ConfigurationService } from '@services/configuration.service';
import { ViewDeterminationService } from './view-determination.service';
import { VenueConfigurationsService } from './venue-configurations.service';


@Injectable({
  providedIn: 'root'
})
export class ViewerService {

  // CONFIGURATION
  private readonly config:                Signal<Configuration>            = inject(APP_CONFIG);
  private readonly dvmConfig:             Signal<Configuration['Dvm']>     = computed((): Configuration['Dvm'] => this.config().Dvm);
  private readonly uiconfig:              Signal<UiConfiguration>          = inject(UiService).uiConfig;


  // COMPUTED CONFIGURATION
  private readonly seatmapDisplay:        Signal<SeatmapDisplay>           = computed((): SeatmapDisplay => this.uiconfig().SeatmapDisplay);
  private readonly seatLevelEnabled:      Signal<boolean>                  = computed((): boolean => this.uiconfig().SeatLevelDisplay.enabled);
  public  readonly customAvailability:    Signal<CustomAvailability>       = computed((): CustomAvailability => this.uiconfig().CustomAvailability);
  public  readonly randomAvailability:    Signal<RandomAvailability>       = computed((): RandomAvailability => this.uiconfig().RandomAvailability);


  // SERVICES
  private readonly navigation:            NavigationService                = inject(NavigationService);
  private readonly viewService:           ViewService                      = inject(ViewService);
  private readonly filters:               FilterService                    = inject(FilterService);
  private readonly template:              TemplateService                  = inject(TemplateService);
  private readonly configuration:         ConfigurationService             = inject(ConfigurationService);
  private readonly viewDetermination:     ViewDeterminationService         = inject(ViewDeterminationService);
  private readonly venueConfigs:          VenueConfigurationsService       = inject(VenueConfigurationsService);


  // DVM SERVICES
  public readonly viewer:                 MapViewerService                 = inject(VIEWER);


  // VIEWERS INIT CONFIGURATION
  private viewerInitOptions:               MapViewerInitOptions            = {};


  // VIEWERS LOAD CONFIGURATION
  public viewerLoadOptions:                MapViewerLoadMapOptions         = {venue_id: '', map_id: ''};


  // VIEWER STYLES
  public viewerStyles!:                    (MapViewerStylesLevel | undefined)[] | undefined;

  // VIEWER AVAILABILITY
  private availability!:                   string[];
  private gaSections!:                     string[];

  // STATE
  public  readonly view:                   Signal<AppViews|null>           = this.viewService.view;
  public  readonly importMode:             Signal<boolean>                 = this.configuration.isImportPreviewMode;
  private readonly duration:               number                          = 300;


  // GETTERS
  /**
   * Devuelve el venue actualmente cargado en el viewer 2D.
   *
   * @returns Identificador del venue o `null` si el viewer no esta inicializado.
   */
  public  get currentVenue():  string | null {
    return ( this.viewer.isInitialized() && this.viewer.getVenueId() ) ?
      this.viewer.getVenueId()! :
      null;
  }

  /**
   * Devuelve el mapa actualmente cargado en el viewer 2D.
   *
   * @returns Identificador del mapa o `null` si el viewer no esta inicializado.
   */
  public  get currentMap():    string | null {
    return ( this.viewer.isInitialized() && this.viewer.getMapId() ) ?
      this.viewer.getMapId()! :
      null;
  }


  // PUBLIC METHODS
  /**
   * Aplica disponibilidad al viewer 2D considerando filtros activos, tipos de nodo y excepciones GA.
   *
   * @param type Tipo de nodo DVM al que aplicar disponibilidad explicita.
   * @param availability Identificadores que deben quedar disponibles para el tipo indicado.
   * @param viewer Instancia de viewer donde aplicar la disponibilidad.
   */
  public setAvailability(type?: string, availability?: string[], viewer: MapViewerService = this.viewer): void {

    // Si hay filtros, obtenemos los filtros por vista, si no devolvemos undefined.
    const filteredAvailability: string[] | undefined = this.filters.getFiltersByView(this.view()!).length ? this.filters.getFiltersByView(this.view()!) : undefined;

    // Si hay type y availabilty explícitos, los seteamos directamente.
    if(type && availability){

      viewer.setAvailability(type, availability)

    }
    // Si no viene ningun param
    else {

      // Iterate over each node type on the map. For each type, use the specific filter if one exists,
      // otherwise set all nodes of that type as available.
      const sectionFilter: string[] = this.filters.getFilterByKey(AppNodes.Section as keyof Filter);
      viewer.getTypesList().forEach((t) => {
        const filterKeys:     AppNodes[] = [AppNodes.Section, AppNodes.Row, AppNodes.Seat];
        const isStandardType: boolean    = filterKeys.includes(t as AppNodes);
        // For seats, merge explicit seat filter with seats derived from the row filter.
        const typeFilter = isStandardType
          ? (t === AppNodes.Seat ? this.resolveRowSeats(viewer) : this.filters.getFilterByKey(t as keyof Filter))
          : [];

        // For non-standard types (area, areaprice, etc.) with an active section filter,
        // only keep available the section filter IDs that belong to that type.
        // GA is excluded from this logic and always keeps all its nodes available.
        // The viewer === this.viewer check ensures this logic does not affect the minimap,
        // which shares the same FilterService but should not be restricted by the menu filter.
        if (!isStandardType && t !== AppNodes.Ga && sectionFilter.length && viewer === this.viewer) {
          const idsOfType: string[] = viewer.getNodesByType(t).map(n => n.id!);
          viewer.setAvailability(t, sectionFilter.filter(id => idsOfType.includes(id)));
        } else {
          const resolved = availability ?? (typeFilter.length ? typeFilter : viewer.getNodesByType(type ?? t));
          viewer.setAvailability(type ?? t, resolved);
        }
      });

    }

    // Finalmente, si habia filtros, seleccionamos los nodos establecidos en los filtros.
    if(filteredAvailability && this.view() !== AppViews.Seatmap ) this.viewer.select(filteredAvailability);

  }

  /**
   * Carga un mapa 2D, actualiza la vista actual y reaplica disponibilidad, flags, navegacion y estilos.
   *
   * @param mapId Identificador del mapa a cargar. Si se omite, usa `viewerLoadOptions.map_id`.
   * @param type Tipo de nodo para disponibilidad explicita durante la carga.
   * @param availability Identificadores disponibles para el tipo indicado.
   * @returns Observable emitido por la carga del mapa DVM.
   */
  public loadMap(mapId?: string, type?: string, availability?: string[]): Observable<MapViewerLoadObject> {

    const options: MapViewerLoadMapOptions = mapId ? Object.assign({}, this.viewerLoadOptions, {map_id: mapId}) : this.viewerLoadOptions;

    if(this.importMode() && !this.viewerStyles) this.setViewerInitStyles(this.dvmConfig().viewer.styles);

    return from(this.viewer.loadMap(options)).pipe(
      tap((): void => {
        // Seteamos la vista actual
        this.viewDetermination.setCurrentView(false);

        // Seteamos disponibilidad
        this.setAvailability(type, availability);

        // Seteamos los flags del viewer
        this.setViewerFlags(this.dvmConfig().misc.flags?.viewerFlags);

        // Registramos la navegación
        this.registerNavigation(options.map_id);

        // Si hay estilos, los seteamos
        if(this.viewerStyles) this.setViewerStyles(this.viewerStyles);

        //if bestNodes enabled, pre-select best nodes
        if(this.seatmapDisplay().enabled && this.seatmapDisplay().bestNode && this.view() === AppViews.Seatmap) this.selectBestNodes();

        //if customAvailability enabled, set given nodes as unavailable
        if(this.customAvailability().enabled) this.handleCustomAvailaibilityData();

        //if randomAvailability enabled, ste availability per node type
        if(this.randomAvailability().enabled) this.handleRandomAvailablity();
      }),
      catchError((error: unknown) => { throw error })
    )

  }

  /**
   * Centra el viewer 2D usando un factor de escala fijo solo en mapas Main.
   *
   * @param target Nodo, nodos, bounding box o posicion de escena de destino.
   * @returns Promesa emitida por la navegacion DVM.
   */
  public goTo(target: Parameters<MapViewerService['goTo']>[0]): Promise<boolean> {
    return this.viewer.goTo(target, this.view() === AppViews.Main ? 5 : undefined);
  }

  /**
   * Descarta la plantilla de estilos almacenada para futuras cargas o reaplicaciones.
   */
  public clearStyles(): void {
    this.viewerStyles = undefined;
  }

  /**
   * Reinicia el historial de navegacion y, opcionalmente, recarga el mapa inicial configurado.
   *
   * @param loadMap Indica si debe recargar el mapa inicial cuando el mapa actual difiere.
   */
  public resetViewer(loadMap: boolean = true): void {

    // Reseteamos el historial de navegación
    this.navigation.reset();

    if((this.currentMap !== this.viewerLoadOptions.map_id) && loadMap) this.loadMap().subscribe({
      error: (error: unknown): void => console.error('Error resetting viewer map', error)
    }); // Si el mapa no es el mismo que el inicial, lo cargamos de nuevo.

  }


  // PRIVATE METHODS
  /**
   * Inicializa el MapViewer si es necesario y prepara las opciones de carga al quedar listo.
   */
  public initMapViewer(): void {

    // Si no está inicializado el viewer, lo inicializamos.
    if(!this.viewer.isInitialized()) {

      this.setViewerInitialConfig();

      this.viewer.initialize(this.viewerInitOptions).subscribe({
        error: (error: unknown): void => console.error('Error initializing map viewer', error)
      });

    }

    // Esperamos que encienda y cargamos las load options
    const viewerReady$: Observable<MapViewerService> = this.viewer.isInitialized()
      ? of(this.viewer)
      : this.viewer.waitInitialize().pipe(first());

    viewerReady$.subscribe((): void => {

      // Configuracion efectiva, no `Dvm.viewer` crudo: respeta la venue configuration seleccionada
      // sin escribir nada en la config almacenada.
      this.setViewerLoadOptions(this.venueConfigs.viewerConfig());

    })

  }

  /**
   * Construye la configuracion inicial del viewer 2D: estilos por grupos, plugins y estilos base.
   */
  private setViewerInitialConfig(): void {

    const config: Configuration['Dvm'] = this.dvmConfig();
    this.viewerInitOptions.styles_by_groups = true;

    this.setViewersPlugins(config.misc);

    if(config.viewer.styles){
      this.setViewerInitStyles(config.viewer.styles);
    }

  }

  /**
   * Actualiza `viewerLoadOptions` con el venue y mapa definidos en la configuracion DVM.
   *
   * @param config Configuracion del viewer DVM.
   */
  public setViewerLoadOptions(config: Configuration['Dvm']['viewer']): void{

    this.viewerLoadOptions.venue_id   = config.venueId;
    this.viewerLoadOptions.map_id     = config.mapId;

  }

  /**
   * Define los plugins del viewer 2D desde configuracion o usa los plugins por defecto.
   *
   * @param config Configuracion miscelanea DVM con plugins del viewer.
   */
  private setViewersPlugins(config: Configuration['Dvm']['misc']): void {

    const plugins = config.plugins;

    // MAP VIEWER PLUGINS
    this.viewerInitOptions.plugins = plugins.viewerPlugins?.length ? plugins.viewerPlugins : this.setDefaultPlugins();

  }

  /**
   * Devuelve la lista de plugins base usados por el viewer 2D.
   *
   * @returns Claves de plugins DVM activadas por defecto.
   */
  private setDefaultPlugins(): (keyof MapViewerPlugins)[] {

    const plugins: (keyof MapViewerPlugins)[] = [];

    plugins.push(
      'navigable_minimap',
      'best_nodes',
    );

    return plugins;

  }

  /**
   * Aplica flags al viewer 2D o restaura los flags por defecto si no se reciben valores.
   *
   * @param flags Flags del SDK DVM que deben sobrescribirse.
   */
  private setViewerFlags(flags?: MapViewerFlagsAPI): void {

    if(!flags) return this.setDefaultFlags();

    Object.entries(flags).forEach(([key, value]): void => {

      if(Object.prototype.hasOwnProperty.call(this.viewer.flags, key))  this.viewer.flags[key as keyof MapViewerFlagsAPI] = value;

    });

  }

  /**
   * Define los flags base del viewer 2D para el comportamiento del minisite.
   */
  private setDefaultFlags(): void {
    this.viewer.flags.fixed_aspect_ratio      = false;
    this.viewer.flags.automatic_selection     = false;
  }

  /**
   * Fusiona estilos configurados con los estilos actuales del viewer y los aplica sin recargar mapa.
   *
   * @param styles Niveles de estilos DVM que deben sobrescribirse.
   */
  public setViewerStyles(styles?: MapViewerStylesLevel | (MapViewerStylesLevel | undefined)[]): void {

    const normalizedStyles: (MapViewerStylesLevel | undefined)[] = this.normalizeViewerStyles(styles);

    if(!normalizedStyles.length) return;

    const currentStyles: MapViewerStylesLevel[] = this.viewer.getStyles();

    if(!Array.isArray(currentStyles)) return;

    let hasAppliedStyles = false;
    
    normalizedStyles.forEach((style: MapViewerStylesLevel | undefined, index: number): void => {

      if(!this.isViewerStyleLevel(style)) return;
      if(!this.isViewerStyleLevel(currentStyles[index])) return;
      
      const keys: string[] = Object.keys(currentStyles[index]);

      if(!Object.keys(style).some(key => keys.includes(key))) return;

      keys.forEach((key: string) => {

        if(key in style) currentStyles[index][key] = style[key];

      })

      hasAppliedStyles = true;
      
    });

    if(hasAppliedStyles) this.viewer.setStyles(currentStyles, true);

  }

  /**
   * Resuelve y cachea la plantilla de estilos inicial desde el template activo o fallback local.
   *
   * @param styles Opciones de estilos configuradas para el viewer.
   */
  public setViewerInitStyles(styles: ViewerStylesOptions): void {

    const styleKey: ViewerStylesOptions['selected']          = styles.selected ?? 'default',
          template: (MapViewerStylesLevel | undefined)[]     = this.normalizeViewerStyles(this.template.selectedTemplate()?.template),
          fallback: (MapViewerStylesLevel | undefined)[]     = this.normalizeViewerStyles((styles && styles.templates ? styles.templates![styleKey!] : undefined) ?? undefined);

    if(!template.length && !fallback.length) return;

    this.viewerStyles = template.length ? template : fallback;

  }

  /**
   * Normaliza plantillas legacy de estilos para que el runtime siempre trabaje con niveles.
   *
   * @param styles Estilos recibidos desde configuracion, template global o legacy.
   * @returns Array de niveles validos.
   */
  private normalizeViewerStyles(styles?: MapViewerStylesLevel | (MapViewerStylesLevel | undefined)[]): (MapViewerStylesLevel | undefined)[] {

    if(!styles) return [];

    const levels: unknown[] = Array.isArray(styles) ? styles : [styles];

    return levels.map((level): MapViewerStylesLevel | undefined => this.isViewerStyleLevel(level) ? level : undefined);

  }

  /**
   * Comprueba que un nivel de estilos sea un objeto aplicable por DVM.
   *
   * @param style Nivel de estilos a validar.
   * @returns `true` si el nivel puede fusionarse con estilos actuales.
   */
  private isViewerStyleLevel(style: unknown): style is MapViewerStylesLevel {
    return typeof style === 'object' && style !== null && !Array.isArray(style);
  }

  /**
   * Selecciona el mejor asiento sugerido por el plugin `best_nodes` y centra el viewer en el.
   */
  private selectBestNodes(): void {

    if (!this.viewer.best_nodes) return;

    from(this.viewer.best_nodes.getBestNodes({ type: AppNodes.Seat, quantity: 1 })).subscribe({

      next: (nodes: MapViewerNode[]): void => {

        if (!nodes || nodes.length === 0) return;

        this.viewer.select(nodes)

        setTimeout(() => this.goTo(nodes[0].id!), this.duration + 250);

      },
      error: (err: unknown) => console.error('Error getting best nodes', err)
    });

  }

  /**
   * Convierte filtros de fila en identificadores de asiento y los combina con filtros de asiento.
   *
   * @param viewer Viewer usado para consultar los nodos de tipo asiento.
   * @returns Lista unica de asientos filtrados.
   */
  private resolveRowSeats(viewer: MapViewerService): string[] {
    
    const {row, seat} = this.filters.filter();
  
    if (!row.length) return seat;

    const seatsFromRows: string[] = viewer.getNodesByType(AppNodes.Seat)    // Obtenemos todos los nodos de tipo asiento
      .map(node => node.id!)                                                // Cogemos solo sus IDs 
      .filter(seatId => row.some(rowId => seatId.startsWith(`${rowId}-`))); // Filtramos los IDs para quedarnos solo con los que pertenecen a las filas seleccionadas (que empiezan por el ID de la fila seguido de un guion)

    return [...new Set([...seatsFromRows, ...seat])];
    
  }

  /**
   * Marca como no disponibles los nodos definidos en `CustomAvailability`.
   */
  public handleCustomAvailaibilityData() {
    const data = this.customAvailability().data;
    if (!data) return;

    const appNodes = this.viewer.getTypesList();
    appNodes.forEach((nodeType) => {
      data[nodeType as AppNodes]?.forEach((id) => {
        this.viewer.setUnavailable(nodeType, id);
      });
    });
  }

  /**
   * Aplica disponibilidad aleatoria a los tipos de nodo configurados para la vista actual.
   */
  public handleRandomAvailablity() {
    const view = this.view();
    const random = this.randomAvailability();
  
    if (!random.enabled || !random.applyTo.length) return;

    random.applyTo.forEach((node) => {
      const percentage =
        node === AppNodes.Ga
          ? random.percentages[AppNodes.Section]! / 100
          : random.percentages[node!]! / 100;

      if (
        (view === AppViews.Topview && (node === AppNodes.Section || node === AppNodes.Ga)) ||
        (view === AppViews.Seatmap && node === AppNodes.Seat)
      ) {
        this.viewer.setAvailability(node, this.getRandomAvailability(node, percentage));        
      }
    });
    
  }

  /**
   * Calcula una muestra aleatoria de nodos disponibles respetando el estado de sus padres.
   *
   * @param type Tipo de nodo DVM a muestrear.
   * @param availabilityRatio Ratio entre 0 y 1 de nodos que deben quedar disponibles.
   * @returns Nodos seleccionados como disponibles.
   */
  private getRandomAvailability(type: string, availabilityRatio: number): MapViewerNode[] {
    return this.viewer.getNodesByType(type).filter((node) => {
        if (node.parent) {
            const parent = this.viewer.getNodeById(node.parent);
            if (parent && parent.state === "available" || parent && parent.state === "selected") {
                return Math.random() < availabilityRatio;
            } else {
                return false;
            }
        } else {
            return Math.random() < availabilityRatio;
        }
    });
  }


  // NAVIGATION
  /**
   * Registra en el historial interno el mapa cargado y la vista DVM actual.
   *
   * @param mapId Identificador del mapa cargado.
   */
  public registerNavigation(mapId: string | undefined): void {

    if(!mapId) return; // Si no hay mapId, no hacemos nada.

    // Añadimos la vista a la historia de navegación.
    this.navigation.next({
      map_id: mapId,
      view:   this.view()!
    });

  }

  /**
   * Actualiza el control de layers del viewer 2D para mostrar topview o seat level.
   *
   * @param automaticControlLevel Indica si DVM debe controlar automaticamente el nivel.
   * @param level Nivel de layer que debe quedar activo.
   */
  public setLayers(automaticControlLevel: boolean, level: number): void {
    this.viewer.layers.flags.automatic_control_level = automaticControlLevel;
    this.viewer.layers.setLayerLevel(level);
  }

}
